import { apiFetch } from './client';

export interface VerifiedNumberRow {
  id: string;
  user_id: string;
  e164: string;
  verified_at: string | null;
  created_at: string;
  deleted_at: string | null;
}

export interface StartVerificationResponse {
  challenge_id: string;
  expires_at: string;
}

/**
 * Discriminated result from POST /verified-numbers/:id/verify when the
 * server returns 400 ok=false. The dialog uses `kind` to render a tailored
 * error: "wrong PIN, N attempts left" vs "challenge expired, start over"
 * vs "locked — start a new verification".
 */
export type VerifyFailKind =
  | { ok: false; kind: 'no_challenge' }
  | { ok: false; kind: 'expired' }
  | { ok: false; kind: 'wrong'; attemptsLeft: number }
  | { ok: false; kind: 'locked' };
export type VerifyResponse = { ok: true } | VerifyFailKind;

export const verifiedNumbers = {
  list: () => apiFetch<{ rows: VerifiedNumberRow[] }>('/api/verified-numbers'),
  add: (e164: string) =>
    apiFetch<{ row: VerifiedNumberRow }>('/api/verified-numbers', {
      method: 'POST',
      body: JSON.stringify({ e164 }),
    }),
  remove: (id: string) =>
    apiFetch<void>(`/api/verified-numbers/${id}`, { method: 'DELETE' }),

  // Verification (security audit fix #9, 2026-05-16) — mirrors the
  // customer-recording-admin OTP flow. The server originates an outbound
  // call to the candidate number speaking a 4-digit code; the user types
  // it back via /verify.
  startVerification: (id: string, customerId?: string | null) =>
    apiFetch<StartVerificationResponse>(`/api/verified-numbers/${id}/start-verification`, {
      method: 'POST',
      body: JSON.stringify(customerId ? { customer_id: customerId } : {}),
    }),
  // The backend returns 200 on ok, 400 with `{ ok: false, kind }` on fail.
  // apiFetch throws on non-2xx, so we catch + unwrap into the discriminated
  // union so callers can `switch (result.kind)` cleanly.
  verify: async (id: string, pin: string): Promise<VerifyResponse> => {
    try {
      return await apiFetch<VerifyResponse>(`/api/verified-numbers/${id}/verify`, {
        method: 'POST',
        body: JSON.stringify({ pin }),
      });
    } catch (err) {
      const body = (err as { details?: { ok?: boolean; kind?: string; attemptsLeft?: number } })
        .details;
      if (body && body.ok === false && typeof body.kind === 'string') {
        return body as VerifyResponse;
      }
      throw err;
    }
  },
};
